import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../../context';
import './ThemeToggle.css';

type Theme = 'light' | 'dark';

interface ThemeToggleProps {
  variant?: 'icon' | 'full';
  className?: string;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'icon',
  className = '',
}) => {
  const { theme, setTheme, isDark } = useTheme();

  const themes: { value: Theme; label: string; icon: React.ReactNode }[] = [
    { value: 'light', label: 'Light', icon: <Sun size={18} /> },
    { value: 'dark', label: 'Dark', icon: <Moon size={18} /> },
  ];

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  if (variant === 'icon') {
    return (
      <button
        className={`theme-toggle theme-toggle--icon ${className}`}
        onClick={toggleTheme}
        aria-label={`Current theme: ${theme}. Click to switch to ${isDark ? 'light' : 'dark'} mode.`}
        title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      >
        <span className={`theme-toggle__icon ${isDark ? 'theme-toggle__icon--dark' : ''}`}>
          {isDark ? <Moon size={20} /> : <Sun size={20} />}
        </span>
      </button>
    );
  }

  return (
    <div className={`theme-toggle theme-toggle--full ${className}`}>
      <span className="theme-toggle__label">Theme</span>
      <div className="theme-toggle__options">
        {themes.map(({ value, label, icon }) => (
          <button
            key={value}
            className={`theme-toggle__option ${theme === value ? 'theme-toggle__option--active' : ''}`}
            onClick={() => setTheme(value)}
            aria-pressed={theme === value}
            title={label}
          >
            {icon}
            <span className="theme-toggle__option-label">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ThemeToggle;
